import React, { useCallback, useMemo, useRef, useState } from "react";
import { LayoutChangeEvent, Platform, View } from "react-native";
import Animated, {
  LayoutAnimationConfig,
  FadeIn,
  FadeOut,
  LinearTransition,
  useSharedValue,
  useAnimatedStyle,
  withTiming,
} from "react-native-reanimated";
import { NextBestActionCard } from "../../nextBestActionCard/NextBestActionCard";
import { NextBestActionCards } from "../NextBestActionCards";
import { style as nextBestActionGridStyle } from "./NextBestActionGrid.style";

const NEXT_BEST_ACTION_GRID_MAX_ACTIONS = 4;
const DURATION = 300;

const layoutAnimations = {
  entering: FadeIn.duration(DURATION),
  exiting: FadeOut.duration(DURATION),
  layout: LinearTransition.duration(DURATION),
};

const NextBestActionGrid: NextBestActionCards = ({ actions = [], basePath, country, segment }) => {
  const gridActions = useMemo(() => actions.slice(0, NEXT_BEST_ACTION_GRID_MAX_ACTIONS), [actions]);
  const initialActions = useRef(actions);
  const initialRender = initialActions.current === actions;
  const renderWithLayoutAnimations = Platform.OS !== "web" || !initialRender;

  const [cardHeight, setCardHeight] = useState<number>();
  const handleOnCardLayout = useCallback(({ nativeEvent: { layout } }: LayoutChangeEvent) => {
    setCardHeight((height) => Math.max(height || 0, layout.height));
  }, []);

  const gridHeight = useSharedValue(0);
  const handleOnLayout = useCallback(
    ({ nativeEvent: { layout } }: LayoutChangeEvent) => {
      gridHeight.value = layout.height;
    },
    [gridHeight],
  );
  const gridAnimatedStyle = useAnimatedStyle(
    () => ({ height: withTiming(gridHeight.value, { duration: DURATION }) }),
    [gridHeight.value],
  );

  const style = useMemo(() => nextBestActionGridStyle(), []);

  return (
    <LayoutAnimationConfig skipEntering skipExiting>
      <Animated.View style={!initialRender && gridAnimatedStyle}>
        <View style={style.gridContainer} onLayout={handleOnLayout}>
          {gridActions.map((action) => (
            <Animated.View key={action.id} {...(renderWithLayoutAnimations ? layoutAnimations : {})} style={style.card}>
              <View style={{ height: cardHeight }} onLayout={handleOnCardLayout}>
                <NextBestActionCard action={action} basePath={basePath} country={country} segment={segment} />
              </View>
            </Animated.View>
          ))}
        </View>
      </Animated.View>
    </LayoutAnimationConfig>
  );
};

export { NextBestActionGrid, NEXT_BEST_ACTION_GRID_MAX_ACTIONS };
